import { put, select, takeLatest, takeEvery } from 'redux-saga/effects';
import axios from 'axios';
import * as Immutable from 'immutable';
import {
  TutorialActionTypes,
  SyncChapterComplete,
  TutorialCompletionMap,
} from 'Types/TutorialTypes';
import { hydrateCompletion } from 'Actions/tutorial';
import { TutorialReducerState } from 'Reducers/tutorialReducer';
import { awsRum } from 'Utils/AwsRum';
import { actionTypes } from 'Types';

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;

interface TutorialProgressResponse {
  data: TutorialCompletionMap;
}

function hasEntries(completionMap: TutorialCompletionMap): boolean {
  return Object.keys(completionMap).length > 0;
}

type LocalCompleted = Immutable.Map<number, Immutable.Set<number>> | Record<string, number[]>;

function isImmutableMap(
  value: LocalCompleted,
): value is Immutable.Map<number, Immutable.Set<number>> {
  return Immutable.Map.isMap(value);
}

function localCompletedIsEmpty(completed: LocalCompleted): boolean {
  if (isImmutableMap(completed)) {
    return completed.size === 0;
  }
  return Object.keys(completed).length === 0;
}

function localCompletionToChapterList(
  completed: LocalCompleted,
): Array<{ level_number: number; chapter: number }> {
  const entries: Array<{ level_number: number; chapter: number }> = [];
  if (isImmutableMap(completed)) {
    completed.forEach((chapters, level) => {
      chapters.forEach((chapter) => {
        entries.push({ level_number: level, chapter });
      });
    });
  } else {
    Object.entries(completed).forEach(([levelStr, chapters]) => {
      const level = parseInt(levelStr, 10);
      chapters.forEach((chapter) => {
        entries.push({ level_number: level, chapter });
      });
    });
  }
  return entries;
}

function* pushLocalProgressToBackend(completed: LocalCompleted): Generator<unknown, void, unknown> {
  const chapters = localCompletionToChapterList(completed);
  if (chapters.length === 0) return;

  yield axios.post(baseUrl + 'tutorial-progress/bulk', { chapters });
}

function* fetchTutorialProgress(): Generator<unknown, void, unknown> {
  try {
    const response: TutorialProgressResponse = yield axios.get<TutorialCompletionMap>(
      baseUrl + 'tutorial-progress',
    );

    const localState: TutorialReducerState = yield select(({ tutorialReducer }) => tutorialReducer);

    if (!hasEntries(response.data) && !localCompletedIsEmpty(localState.completed)) {
      yield pushLocalProgressToBackend(localState.completed);
    } else {
      yield put(hydrateCompletion(response.data));
    }
  } catch (err) {
    awsRum?.recordError(err);
    console.error('Failed to fetch tutorial progress:', err);
  }
}

function* syncChapterComplete(action: SyncChapterComplete): Generator<unknown, void, unknown> {
  try {
    yield axios.post(baseUrl + 'tutorial-progress', {
      level_number: action.payload.lesson,
      chapter: action.payload.chapter,
    });
  } catch (err) {
    awsRum?.recordError(err);
    console.error('Failed to sync tutorial chapter completion:', err);
  }
}

export default [
  takeLatest(actionTypes.AUTHENTICATED_STARTUP, fetchTutorialProgress),
  takeEvery(TutorialActionTypes.SYNC_CHAPTER_COMPLETE, syncChapterComplete),
];
